import Flickity from 'flickity'
import { VisibilityElement } from '../custom-elements/visibility'
import { Cart as CartType, UnifiedItem } from './cart.types'
import { formatMoney } from './utils'

export class PWP {
  static PRODUCT_WITH_PURCHASE_ID = 'product-with-purchase'
  static PRODUCT_WITH_PURCHASE_SELECTOR = '[data-id="product-with-purchase"]'
  static PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY = 'pwp-display-history'
  static CAROUSEL_SELECTOR = 'x-ss-carousel-recommend'
  static ADDED_ITEM_SELECTOR = '[data-pwp-added-item]'
  static SHIPPING_SELECTOR = '[data-pwp-shipping]'
  static resizeHandler = null as (() => void) | null

  /**
   * Populates the "recently added to cart" block inside the PWP drawer with the
   * image, title and price of the line item that was just added to the cart.
   */
  static populateAddedItem(item?: Partial<UnifiedItem>): void {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase || !item) return

    const container = productWithPurchase.querySelector<HTMLElement>(PWP.ADDED_ITEM_SELECTOR)
    if (!container) return

    const image = container.querySelector<HTMLImageElement>('[data-pwp-added-image]')
    const title = container.querySelector<HTMLElement>('[data-pwp-added-title]')
    const price = container.querySelector<HTMLElement>('[data-pwp-added-price]')
    const variant = container.querySelector<HTMLElement>('[data-pwp-added-variant]')
    const link = container.querySelector<HTMLAnchorElement>('[data-pwp-added-link]')
    const itemDetails = item as Partial<UnifiedItem> & {
      customer_facing_product_name?: string
      product_colour_name?: string
    }
    const itemTitle = itemDetails.customer_facing_product_name || item.product_title || item.title || ''

    const imageUrl = item.featured_image?.url || item.image
    if (image && imageUrl) {
      image.src = imageUrl
      image.alt = item.featured_image?.alt || itemTitle
    }
    if (link && item.url) link.href = item.url
    if (title) {
      title.textContent = itemTitle
      if (title instanceof HTMLAnchorElement && item.url) title.href = item.url
    }
    if (variant) {
      const values = item.product_has_only_default_variant
        ? []
        : (item.options_with_values ?? []).map((option) => option.value).filter(Boolean)
      const colourName = itemDetails.product_colour_name || ''

      variant.replaceChildren()
      if (colourName) {
        const colour = document.createElement('span')
        colour.className = 'capitalize'
        colour.textContent = colourName
        variant.append(colour)
        if (values.length > 0) {
          const separator = document.createElement('span')
          separator.className = 'px-0.5'
          separator.textContent = ' | '
          variant.append(separator)
        }
      }
      values.forEach((value, index) => {
        if (index > 0) {
          const separator = document.createElement('span')
          separator.className = 'px-0.5'
          separator.textContent = ' | '
          variant.append(separator)
        }
        variant.append(value)
      })
      variant.classList.toggle('hidden', !colourName && values.length === 0)
    }
    if (price) {
      price.replaceChildren()
      const linePrice = item.line_price
      const originalLinePrice = item.original_line_price
      const compareAtPrice = item.compare_at_price
      const quantity = item.quantity ?? 1
      const hasDiscount = (item.discounts?.length ?? 0) > 0 && originalLinePrice !== linePrice
      const hasCompareAt = typeof compareAtPrice === 'number' &&
        typeof item.original_price === 'number' &&
        compareAtPrice > item.original_price

      const appendPrice = (amount: number, className: string, text = formatMoney(amount)) => {
        const line = document.createElement('p')
        line.className = className
        line.textContent = text
        price.append(line)
      }

      const saleClass = (amount: number) =>
        `paragraph-xs lg:paragraph-s font-medium ${amount === 0 ? 'text-system-success' : 'text-brand-sale'}`
      const saleText = (amount: number) => (amount === 0 ? 'Free' : formatMoney(amount))

      if (hasDiscount && typeof originalLinePrice === 'number' && typeof linePrice === 'number') {
        appendPrice(linePrice, saleClass(linePrice), saleText(linePrice))
        appendPrice(originalLinePrice, 'paragraph-xs lg:paragraph-s text-neutral-primary line-through')
      } else if (hasCompareAt && typeof compareAtPrice === 'number' && typeof linePrice === 'number') {
        appendPrice(linePrice, saleClass(linePrice), saleText(linePrice))
        appendPrice(compareAtPrice * quantity, 'paragraph-xs lg:paragraph-s text-neutral-primary line-through')
      } else if (typeof originalLinePrice === 'number') {
        appendPrice(
          originalLinePrice,
          `paragraph-xs lg:paragraph-s font-medium ${originalLinePrice === 0 ? 'text-system-success' : ''}`,
          originalLinePrice === 0 ? 'Free' : formatMoney(originalLinePrice)
        )
      } else {
        const amount = item.final_price ?? item.price
        if (typeof amount === 'number') appendPrice(amount, 'paragraph-xs lg:paragraph-s font-medium')
      }
    }

    container.classList.remove('hidden')
    container.classList.add('flex')
  }

  /**
   * Updates the free shipping message inside the PWP drawer against the latest cart total.
   */
  static updateShippingBar(cart?: CartType): void {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase || typeof cart?.total_price !== 'number') return

    const container = productWithPurchase.querySelector<HTMLElement>(PWP.SHIPPING_SELECTOR)
    if (!container) return

    const minimum = parseInt(container.dataset.freeShippingMinimum || '', 10)
    if (!minimum) return

    const remaining = minimum - cart.total_price

    container.dataset.reached = String(remaining <= 0)

    const remainingText = container.querySelector<HTMLElement>('[data-pwp-shipping-remaining]')
    if (remainingText) remainingText.textContent = formatMoney(Math.max(remaining, 0))
  }

  static showProductWithPurchase(addedItem?: Partial<UnifiedItem>, cart?: CartType): boolean {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)

    // If PWP element doesn't exist or there's no global PWP data, exit early
    if (!productWithPurchase || !window.globals?.metaobjects?.product_with_purchase) {
      return false
    }

    const pdpProductId = (productWithPurchase as HTMLElement).dataset.productId
    if (pdpProductId && String(addedItem?.product_id) !== pdpProductId) {
      return false
    }

    const pwpData = window.globals.metaobjects.product_with_purchase
    const retriggerDelay = pwpData.retrigger_delay

    // Get last display time from session storage
    let lastDisplayed: Date | null = null
    try {
      const storageData = localStorage.getItem(PWP.PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY)
      if (storageData) {
        lastDisplayed = new Date(storageData)
      }
    } catch (error) {
      console.error('Error parsing PWP display history:', error)
    }

    const now = new Date()

    // Check if we should show the PWP based on retrigger delay
    let shouldShow = true

    if (retriggerDelay > 0 && lastDisplayed) {
      // Calculate hours since last display
      const hoursSinceLastDisplay = Math.floor((now.valueOf() - lastDisplayed.valueOf()) / (1000 * 60 * 60))
      // Only show if enough hours have passed since last display
      shouldShow = hoursSinceLastDisplay >= retriggerDelay
    }

    if (shouldShow) {
      PWP.populateAddedItem(addedItem)
      PWP.updateShippingBar(cart)

      // Show the PWP
      VisibilityElement.dispatchShow(PWP.PRODUCT_WITH_PURCHASE_ID)

      // Update display history with current timestamp
      localStorage.setItem(PWP.PRODUCT_WITH_PURCHASE_SESSION_STORAGE_KEY, now.toISOString())

      PWP.reloadFlickity()
      PWP.setupResizeHandler()
      return true
    }

    return false
  }

  static reloadFlickity() {
    const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
    if (!productWithPurchase) return
    const carouselContainer = productWithPurchase.querySelector(PWP.CAROUSEL_SELECTOR)
    if (carouselContainer) {
      const flickityElement = carouselContainer.querySelector('.flickity-enabled')
      if (flickityElement) {
        setTimeout(() => {
          const flickityInstance = Flickity.data(flickityElement)
          if (flickityInstance) {
            flickityInstance.resize()
            flickityInstance.reloadCells()
          }
        }, 300)
      }
    }
  }

  static setupResizeHandler() {
    PWP.removeResizeHandler()
    let resizeTimeout: ReturnType<typeof setTimeout>
    PWP.resizeHandler = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        const productWithPurchase = document.querySelector(PWP.PRODUCT_WITH_PURCHASE_SELECTOR)
        if (productWithPurchase?.getAttribute('data-visible') === 'true') {
          PWP.reloadFlickity()
        }
      }, 150)
    }

    window.addEventListener('resize', PWP.resizeHandler)
  }

  static removeResizeHandler() {
    if (PWP.resizeHandler) {
      window.removeEventListener('resize', PWP.resizeHandler)
      PWP.resizeHandler = null
    }
  }
}
